import * as React from 'react';

import { atom } from 'jotai';
import { useAtomValue, useSetAtom } from 'jotai';

import { presenceAnnounceAtom } from '~/modules/settings/_graphql/mutationPresenceAnnounce';
import {
  type PresenceMember,
  useUserPresence,
} from '~/modules/settings/_graphql/subscriptionPresenceEvents';

type PresenceMemberMap = Map<string, PresenceMember>;

const THROTTLE_MS = 200;

export const $sessionId = atom(() => crypto.randomUUID());
const $announce = presenceAnnounceAtom({});

export type UsePresenceResult = {
  members: PresenceMemberMap;
  announce: (metadata: Record<string, unknown>) => void;
  isSelf: (memberId: string) => boolean;
  isConnected: boolean;
  sessionId: string;
};

export const usePresence = (
  room: string,
  options?: { join?: boolean },
): UsePresenceResult => {
  const join = options?.join ?? true;
  const hasReceivedEventRef = React.useRef(false);
  const sessionId = useAtomValue($sessionId);

  const { membersById } = useUserPresence(join ? room : undefined, {
    skip: !join,
    sessionId,
    onEvent: () => {
      if (!hasReceivedEventRef.current) {
        hasReceivedEventRef.current = true;
        setIsConnected(true);
      }
    },
  });

  const [isConnected, setIsConnected] = React.useState(false);

  const announceMutation = useSetAtom($announce);

  const lastAnnounceRef = React.useRef(0);
  const pendingAnnounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastFocusedFieldRef = React.useRef<string | null>(null);

  const announce = React.useCallback(
    (metadata: Record<string, unknown>) => {
      if ('focusedField' in metadata) {
        lastFocusedFieldRef.current =
          typeof metadata.focusedField === 'string'
            ? metadata.focusedField
            : null;
      }

      const now = Date.now();
      const elapsed = now - lastAnnounceRef.current;

      if (pendingAnnounceRef.current !== null) {
        clearTimeout(pendingAnnounceRef.current);
        pendingAnnounceRef.current = null;
      }

      if (elapsed >= THROTTLE_MS) {
        lastAnnounceRef.current = now;
        announceMutation({ room, metadata, sessionId });
      } else {
        pendingAnnounceRef.current = setTimeout(() => {
          lastAnnounceRef.current = Date.now();
          pendingAnnounceRef.current = null;
          announceMutation({ room, metadata, sessionId });
        }, THROTTLE_MS - elapsed);
      }
    },
    [room, announceMutation, sessionId],
  );

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (pendingAnnounceRef.current !== null) {
          clearTimeout(pendingAnnounceRef.current);
          pendingAnnounceRef.current = null;
        }
        lastAnnounceRef.current = 0;
        announceMutation({ room, metadata: {}, sessionId });
      } else if (document.visibilityState === 'visible') {
        if (lastFocusedFieldRef.current) {
          announceMutation({
            room,
            metadata: { focusedField: lastFocusedFieldRef.current },
            sessionId,
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [room, announceMutation, sessionId]);

  React.useEffect(
    () => () => {
      if (pendingAnnounceRef.current !== null) {
        clearTimeout(pendingAnnounceRef.current);
        pendingAnnounceRef.current = null;
      }
      announceMutation({ room, metadata: {}, sessionId });
    },
    [room, announceMutation, sessionId],
  );

  const isSelf = React.useCallback(
    (memberId: string) => memberId.endsWith(`:${sessionId}`),
    [sessionId],
  );

  return {
    members: membersById,
    announce,
    isSelf,
    isConnected,
    sessionId,
  };
};
